import { useSyncExternalStore } from 'react'
import { z } from 'zod'

const trackingConsentStorageKey = 'trackingConsent'

const trackingConsentMaxAgeMs = 1000 * 60 * 60 * 24 * 365

const storedTrackingConsentSchema = z.object({
  trackingConsent: z.boolean().optional(),
  lastConsentDate: z.string().optional(),
})

type StoredTrackingConsent = z.infer<typeof storedTrackingConsentSchema>

const trackingConsentChanges = new EventTarget()

const trackingConsentChangeEvent = 'change'

const parseJson = (value: string): unknown => {
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}

const readStoredTrackingConsent = (): StoredTrackingConsent => {
  const storedConsent = storedTrackingConsentSchema.safeParse(
    parseJson(localStorage.getItem(trackingConsentStorageKey) || '{}')
  )

  if (storedConsent.success) {
    return storedConsent.data
  }

  localStorage.removeItem(trackingConsentStorageKey)
  return {}
}

export const isTrackingConsentExpired = () => {
  const { lastConsentDate } = readStoredTrackingConsent()
  const lastConsentTime = lastConsentDate
    ? new Date(lastConsentDate).getTime()
    : Number.NaN

  return (
    Number.isNaN(lastConsentTime) ||
    Date.now() - lastConsentTime > trackingConsentMaxAgeMs
  )
}

export const hasTrackingConsent = () =>
  !isTrackingConsentExpired() &&
  readStoredTrackingConsent().trackingConsent === true

export const saveTrackingConsent = (consent: boolean) => {
  localStorage.setItem(
    trackingConsentStorageKey,
    JSON.stringify({
      trackingConsent: consent,
      lastConsentDate: new Date().toISOString(),
    })
  )
  trackingConsentChanges.dispatchEvent(new Event(trackingConsentChangeEvent))
}

const subscribeToTrackingConsent = (listener: () => void) => {
  trackingConsentChanges.addEventListener(trackingConsentChangeEvent, listener)
  return () =>
    trackingConsentChanges.removeEventListener(
      trackingConsentChangeEvent,
      listener
    )
}

export const useTrackingConsent = () =>
  useSyncExternalStore(subscribeToTrackingConsent, hasTrackingConsent)
